/**
 * System API for web mode
 */

import { httpClient } from './httpClient'
import type { ApiResponse } from '../types'
import type { components } from '../../../../../types/flex-api'

type System = components['schemas']['System']
export type SystemWithAssets = System

/**
 * Check if a system exists
 */
export async function get(systemID: string): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().get(`/systems/${systemID}`, { params: { include: '-' } })
    return { success: true }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get system',
      errorCode: error.code || 'UNKNOWN_ERROR',
    }
  }
}

/**
 * Get systems in a pool using the paginated V1 API
 */
export async function getByPoolPaginated(
  poolID: string,
  opts: { limit: number; offset: number }
): Promise<ApiResponse<components['schemas']['PaginatedSystems']>> {
  try {
    const response = await httpClient.getClient().get<components['schemas']['PaginatedSystems']>(`/pools/${poolID}/systems`, {
      params: { include: 'assets', limit: opts.limit, offset: opts.offset },
      headers: { Accept: 'application/vnd.gridx.flex.v1+json' },
    })
    return { success: true, data: response.data }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get systems',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get all systems in a pool
 */
export async function getByPool(poolID: string): Promise<ApiResponse<SystemWithAssets[]>> {
  try {
    const response = await httpClient.getClient().get<{ systems: SystemWithAssets[] }>(`/pools/${poolID}/systems`, {
      params: { include: 'assets' },
    })
    return {
      success: true,
      data: response.data.systems || [],
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get systems',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Add a system to a pool
 */
export async function add(poolID: string, systemID: string, activeFrom?: string): Promise<ApiResponse<System>> {
  try {
    const requestBody: { systemID: string; activeFrom?: string } = { systemID }

    // Only include activeFrom if provided (API defaults to "now" if omitted)
    if (activeFrom) {
      requestBody.activeFrom = activeFrom
    }

    const response = await httpClient.getClient().post<System>(`/pools/${poolID}/systems`, requestBody)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    const message =
      error.code === 'FORBIDDEN'
        ? 'Not found or insufficient permissions'
        : error.message || 'Failed to add system'
    return {
      success: false,
      error: message,
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Remove a system from a pool
 */
export async function remove(poolID: string, systemID: string): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().delete(`/pools/${poolID}/systems/${systemID}`)
    return {
      success: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to remove system',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get pool IDs that a system belongs to
 */
export async function getPoolsBySystem(systemID: string): Promise<ApiResponse<string[]>> {
  try {
    const response = await httpClient.getClient().get<string[]>(`/pools/systems/${systemID}/owners`)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get pools by system',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get system history (timeline of INSERT/DELETE events)
 */
export async function getHistory(systemID: string): Promise<ApiResponse<{
  events: Array<{
    poolID: string
    timestamp: string
    eventType: 'INSERT' | 'DELETE'
  }>
}>> {
  try {
    const response = await httpClient.getClient().get<{
      events: Array<{
        poolID: string
        timestamp: string
        eventType: 'INSERT' | 'DELETE'
      }>
    }>(`/pools/systems/${systemID}/history`)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get system history',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get system IDs in a pool (lightweight endpoint)
 */
export async function getSystemIds(
  poolID: string,
  listAll: boolean = true
): Promise<ApiResponse<string[]>> {
  try {
    const response = await httpClient.getClient().get<string[]>(`/pools/${poolID}/system-ids`, {
      params: { listAll },
    })
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get system IDs',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Update system active times in a pool
 */
export async function updateSystem(
  poolID: string,
  systemID: string,
  data: {
    activeFrom?: string
    activeUntil?: string | null
  }
): Promise<ApiResponse<System>> {
  try {
    const response = await httpClient.getClient().patch<System>(
      `/pools/${poolID}/systems/${systemID}`,
      data
    )
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update system',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}
